import { useEffect, useRef, useState } from 'react'

const LOOK_AHEAD_ROOT_MARGIN = '0px 0px 600px 0px'

type AutoLoadPager = {
  items: readonly unknown[]
  dataUpdatedAt: number
  hasNextPage: boolean
  isFetching: boolean
  isFetchingNextPage: boolean
  isFetchNextPageError: boolean
  autoLoad: () => void
}

export function useAutoLoadMore(
  pager: AutoLoadPager,
  { root, paused = false }: { root: HTMLElement | null; paused?: boolean },
) {
  const [sentinel, setSentinel] = useState<HTMLElement | null>(null)
  const autoLoadRef = useRef(pager.autoLoad)
  useEffect(() => {
    autoLoadRef.current = pager.autoLoad
  })
  const armed =
    sentinel !== null
    && root !== null
    && pager.hasNextPage
    && !pager.isFetching
    && !pager.isFetchingNextPage
    && !pager.isFetchNextPageError
    && !paused
  const loadedCount = pager.items.length
  const dataUpdatedAt = pager.dataUpdatedAt

  // A fast response can collapse react-query's fetching and success notifications
  // into one render, so re-arming keys off the settled data rather than the
  // transient fetching flag.
  useEffect(() => {
    if (!armed || sentinel === null || root === null) return
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return
        observer.disconnect()
        autoLoadRef.current()
      },
      { root, rootMargin: LOOK_AHEAD_ROOT_MARGIN },
    )
    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [armed, sentinel, root, loadedCount, dataUpdatedAt])

  return { sentinelRef: setSentinel }
}
